import { EOrderConfirmationActions, EsecondaryPaymentStatus } from '../OrderConfirmation.types';
import {
  PostOrderDetailsInfo,
  EPostOrderDataActions,
  IPostOrderDetailsCitrusInfo,
  GET_SECONDARY_PAYMENT_STATUS_START,
  GET_SECONDARY_PAYMENT_STATUS_SUCCESS,
  GET_PAYMENT_TRANSACTION_STATUS_START,
  GET_PAYMENT_TRANSACTION_STATUS_SUCCESS,
} from 'pages/OrderDetail/orderData.type';
import {
  TApiMiddlewareDispatch,
  EApiMiddlewareMethods,
} from 'config/redux/middlewares/api.middleware.types';
import {
  GET_ORDER_DETAILS_V2,
  GET_SECONDARY_STATUS,
  GET_TRANSACTION_STATUS,
  SEND_ORDERID_TO_CITRUS,
} from 'pages/OrderDetail/constants/apis.constants';
import { showNotification } from 'common/Notification/redux/notification.action';
import { IAppState } from '@mafc/common';
import { notificationTypes } from 'common/Notification/redux/notification.type';
import { trackPaymentFailureLoyalty } from '../utils/orderConfirmation.utils';
import { newRelicCustomEvent } from 'utils/newRelicTracker';
import { NEW_RELIC_EVENT } from 'pages/Checkout/constants/checkout.constant';

export const hydrateReducerWithOrder = (details: any) => ({
  type: EOrderConfirmationActions.HYDRATE_CONFIRMATION_DETAILS,
  payload: { ...details },
});

export const clearReducerContents = () => ({
  type: EOrderConfirmationActions.CLEAR_CONFIRMATION_DETAILS,
});

export const setSecondaryStatusSuccess = (status: EsecondaryPaymentStatus) => ({
  type: GET_SECONDARY_PAYMENT_STATUS_SUCCESS,
  payload: { secondaryPaymentStatus: status },
});

export const setTransactionStatusSuccess = (status: EsecondaryPaymentStatus) => ({
  type: GET_PAYMENT_TRANSACTION_STATUS_SUCCESS,
  payload: { paymentTransactionStatus: status },
});

export const orderDetailLoading = (loading: boolean) => ({
  type: EOrderConfirmationActions.ORDER_DETAIL_LOADING,
  payload: { orderDetailLoading: loading },
});

export const getSecondaryStatus = ({
  currentOrderID,
  onSuccessCallback,
  onFailureCallback,
}: PostOrderDetailsInfo) => (dispatch: TApiMiddlewareDispatch, getState: () => IAppState) => {
  dispatch({
    type: GET_SECONDARY_PAYMENT_STATUS_START,
    payload: {
      url: GET_SECONDARY_STATUS,
      method: EApiMiddlewareMethods.GET,
      params: {
        currentOrderID,
      },
      headers: {
        'channel-code': process.env.REACT_APP_CHANNEL_CODE,
        'x-api-key': process.env.REACT_APP_SECRET_KEY,
      },
      onSuccess: res => {
        const { transactionStatus, paymentModeStatusList } = res;
        dispatch(setSecondaryStatusSuccess(transactionStatus) as any);
        trackPaymentFailureLoyalty(paymentModeStatusList);
        if (onSuccessCallback) {
          onSuccessCallback();
        }
      },
      onFailure: e => {
        dispatch(setSecondaryStatusSuccess(EsecondaryPaymentStatus.FAILURE) as any);
        if (onFailureCallback) {
          onFailureCallback(e);
        }
        dispatch(
          showNotification({
            type: notificationTypes.errorCheckout,
            message: `Error getting details for that order number.`,
          }) as any
        );
      },
    },
  });
};

export const getTransactionStatus = ({
  transactionId,
  onSuccessCallback,
  onFailureCallback,
  retryCount = 0,
}: PostOrderDetailsInfo & { retryCount?: number }) => (dispatch: TApiMiddlewareDispatch) => {
  dispatch({
    type: GET_PAYMENT_TRANSACTION_STATUS_START,
    payload: {
      url: `${GET_TRANSACTION_STATUS}/${transactionId}/status`,
      method: EApiMiddlewareMethods.GET,
      apiVersion: 3,
      headers: {
        'channel-code': 'CARREFOUR',
      },
      onSuccess: res => {
        const { status, message } = res;
        if (status === EsecondaryPaymentStatus.PENDING && retryCount < 3) {
          setTimeout(() => {
            dispatch(
              getTransactionStatus({
                transactionId,
                onSuccessCallback,
                onFailureCallback,
                retryCount: retryCount + 1,
              }) as any
            );
          }, 5000);
        }
        dispatch(setTransactionStatusSuccess(status) as any);
        const eventName =
          status === EsecondaryPaymentStatus.SUCCESS
            ? NEW_RELIC_EVENT.TRANSACTION_STATUS_SUCCESS
            : NEW_RELIC_EVENT.TRANSACTION_STATUS_PENDING;
        newRelicCustomEvent(eventName, {
          transactionId,
          message,
        });
        if (onSuccessCallback) {
          onSuccessCallback();
        }
      },
      onFailure: e => {
        dispatch(setTransactionStatusSuccess(EsecondaryPaymentStatus.FAILURE) as any);
        newRelicCustomEvent(NEW_RELIC_EVENT.TRANSACTION_STATUS_FAILED, {
          error: e,
          transactionId,
        });
        if (onFailureCallback) {
          onFailureCallback(e);
        }
        dispatch(
          showNotification({
            type: notificationTypes.errorCheckout,
            message: `Error getting details for that order number.`,
          }) as any
        );
      },
    },
  });
};

export const fetchOrderDetailsFor = ({
  currentOrderID,
  onSuccessCallback,
  onFailureCallback,
}: PostOrderDetailsInfo) => (dispatch: TApiMiddlewareDispatch) => {
  dispatch({
    type: EPostOrderDataActions.GET_ORDER_DETAILS_REQ,
    payload: {
      url: GET_ORDER_DETAILS_V2,
      isAbsUrl: true,
      method: EApiMiddlewareMethods.GET,
      enableProgress: false,
      params: {
        currentOrderID,
      },
      onSuccess: onSuccessCallback,
      onFailure: e => {
        if (onFailureCallback) {
          onFailureCallback(e);
        }
      },
    },
  });
};

export const sendOrderIdToCitrus = (data: IPostOrderDetailsCitrusInfo) => (
  dispatch: TApiMiddlewareDispatch
) => {
  dispatch({
    type: EPostOrderDataActions.SEND_ORDER_DETAILS_TO_CITRUS,
    payload: {
      url: SEND_ORDERID_TO_CITRUS,
      isAbsUrl: true,
      method: EApiMiddlewareMethods.POST,
      enableProgress: false,
      data,
    },
  });
};
